import { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Grid,
  Box,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material';
import emailSendIcon from '../../../assets/images/email-valid-home.png';
import emailInvalidIcon from '../../../assets/images/email-invalid.png';
import invalidIcon from '../../../assets/images/invalidIcon.png';
import banner from '../../../assets/images/newsLetterBanner/banner.png';
import { useNavigate } from 'react-router-dom';
import OtpModalContainer from './OtpModalContainer';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { checkEmailRequest, resetOtp, resetRedirectionAndOtp } from '../../../redux/slices/userSlice';

const NewsletterBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { redirection, otp } = useSelector((state: any) => state.user);

  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const { i18n, t } = useTranslation();

  const data: any = t('newsLetterBanner', {
    returnObjects: true,
    defaultValue: {}
  });

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    let isInvalid = !re.test(email);

    return isInvalid;
  }

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const isInvalid = validateEmail(value);
    setInvalid(isInvalid);
    if (!isInvalid) {
      // Validate email and enable/disable the button
      dispatch(checkEmailRequest(value));
    }
  };

  useEffect(() => {
    if (redirection) {
      navigate(`/${i18n.language}/seller-profile/user-settings`);
      dispatch(resetRedirectionAndOtp())
    } else if (otp) {
      setIsOtpModalOpen(true);
      dispatch(resetRedirectionAndOtp())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirection, otp]);

  const handleCloseOtpModal = () => {
    setIsOtpModalOpen(false);
    dispatch(resetOtp());
  };

  const handleInputChange = (e) => {
    const email = e.target.value;
    setValue(email);
    invalid && setInvalid(false); // Reset invalid state on input change
  };


  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        mt: '16px',
        backgroundImage: `url(${banner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '16px 16px 0 16px',
        borderRadius: '12px'
      }}
    >
      <Grid item xs={12} md={6} sx={{ mt: '24px' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? '18px' : '28px',
            fontFamily: 'Open Sans',
            lineHeight: isMobile ? '24px' : '48px',
          }}
        >
          {data.heading}
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? '12px' : '18px',
            fontFamily: 'Open Sans',
            mt: '16px',
          }}
        >
          {data.sub_heading}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          justifyContent: isMobile || isTablet ? 'center' : 'flex-end',
          pr: isMobile ? 0 : '40px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            backgroundColor: '#FFFFFF',
            gap: isMobile ? '1rem' : '8px',
            width: isMobile ? '280px' : isTablet ? '648px' : '532px',
            height: isMobile ? 'inherit' : '92px',
            borderRadius: isMobile ? '16px' : '100px',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem'
          }}
        >
          <TextField
            fullWidth
            value={value}
            sx={{
              mx: isMobile ? '' : '24px',
              px: isMobile ? '16px' : '',
              flex: 2,
            }}
            error={invalid} // Show error state if invalid
            helperText={invalid ? <span style={{ display: 'flex', columnGap: '.5rem', paddingLeft: '.25rem' }}><img src={invalidIcon} alt='invalid email icon' />{data.emailError}</span> : isMobile ? '' : ' '} // Show error message if invalid
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(!!value)} // Keep label if there's a value
            label={isFocused || value ? data.placeHolder : ' '}
            placeholder={!isFocused && data.placeHolder} // Placeholder text
            variant="standard" // Standard variant for floating label behavior
            InputLabelProps={{
              sx: {
                fontSize: '12px',
                pl: isMobile ? '1.5rem' : '2.5rem', // Adjust padding for mobile
                color: '#717173', // Black color for the label
                '&.Mui-focused': {
                  color: '#717173', // Gray color when focused
                },
              },
            }}
            InputProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  borderBottom: '1px solid #C9CACC', // Default border color
                  '&::placeholder': {
                    color: '#717173', // Placeholder color
                    opacity: 1, // Ensure the placeholder is fully visible
                    fontSize: '14px'
                  },
                },
                '&:after': {
                  borderBottom: '1px solid #C9CACC', // Default border color
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: '1px solid #C9CACC', // Gray border on hover
                },
                '&.Mui-focused:before': {
                  borderBottom: '1px solid #C9CACC', // Gray border when focused
                },
              },
              startAdornment: (
                <InputAdornment position="start" sx={{ mt: isFocused ? '-1.25rem' : 'unset' }}>
                  <img
                    src={invalid ? emailInvalidIcon : emailSendIcon}
                    alt="email icon"
                    style={{
                      width: isMobile ? '16px' : '20px', // Adjust icon size for mobile
                      height: isMobile ? '12px' : '16px',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            aria-required="true"
          />
          <Button
            variant="contained"
            type='submit'
            onClick={handleSubmit}
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '24px',
              width: isMobile ? '85%' : '162px',
              minWidth: '160px',
              height: '44px',
              mr: isMobile ? 0 : '24px',
              // py: '12px',
              backgroundColor: '#1460AA',
              textTransform: 'none',
              lineHeight: '20px'
            }}
          >
            {data.button}
          </Button>
        </Box>{' '}
      </Grid>
      <Grid item xs={12} sx={{ mt: isMobile ? '24px' : '40px' }}>
        <Divider
          sx={{
            borderColor: '#C9CACC',
            height: '1px',
            ml: '-32px',
            mr: '-16px'
          }}
        />
        <Typography
          sx={{
            fontWeight: 600,
            fontFamily: 'Open Sans',
            fontSize: '18px',
            lineHeight: '24px',
            my: isMobile ? '24px' : '40px',
          }}
        >
          {data.bottom}&nbsp;
          <Button
            type='button'
            onClick={() => navigate(`/${i18n.language}/notifications-preferences`)}
            sx={{
              cursor: 'pointer',
              border: 'none',
              color: '#1460AA',
              padding: 0,
              textAlign: isMobile ? 'left' : 'auto',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '16px',
              fontFamily: 'Open Sans',
              mt: '-2px',
              ml: { xs: 0, }
            }}
          >
            {data.link}
          </Button>
        </Typography>
      </Grid>
      {isOtpModalOpen && <OtpModalContainer open={isOtpModalOpen} handleClose={handleCloseOtpModal} email={value} />}
    </Grid>
  );
};

export default NewsletterBanner;
